// react and mui stuff
import { useContext, useEffect, useState } from "react"
import YouTube from "react-youtube"
import { ToastContext } from "../../components/toast";
import { UserContext } from "../../utils/contexts";

import Button from "@mui/joy/Button"
import Card from "@mui/joy/Card"
import FormControl from "@mui/joy/FormControl"
import Radio from "@mui/joy/Radio"
import RadioGroup from "@mui/joy/RadioGroup"
import Typography from "@mui/joy/Typography"

// data and stuff
import { CliUser } from "../../types/users";
import { SortQuestion, SortResults } from "../../types/daedalus";
import { daedalusAPI, imgSrc } from "../../utils/backend";
import { updateUser } from "../../utils/utils";
import { setData } from "../../utils/setdata";

const allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";

function Questions({ questions, sortFunction, disabled }: { questions: SortQuestion[], sortFunction: (e: React.FormEvent<HTMLFormElement>) => any, disabled: boolean }) {
  useEffect(() => {
    document.getElementById("survey")?.scrollIntoView({ behavior: "smooth" });
  }, [])

  return (
    <div id="survey">
      <br/>
      <Typography level="h1" textAlign="center" mb="25px">House Sorting Questions</Typography>
      <form onSubmit={sortFunction}>
        {questions?.map((q, y) => (
          <Card key={q.question} sx={{ maxWidth: "600px", margin: "auto", marginBottom: "15px", backgroundColor: "var(--semi-transparent)" }}>
            <FormControl required>
              <Typography id={`${y}label`} level="title-md">{q.question}</Typography>
              <RadioGroup name={y.toString()} aria-labelledby={`${y}label`}>
                {q.answers.map((a, x) => <Radio required label={a} key={a} value={x}/>)}
              </RadioGroup>
            </FormControl>
          </Card>
        ))}
        <div style={{ maxWidth: "600px", margin: "auto"}}>
          <Button disabled={disabled} fullWidth size="lg" sx={{ marginTop: "50px"}} type="submit">Sort me!</Button>
        </div>
        <br id="results"/>
      </form>
    </div>
  )
}

function Results({ results, setUser, user }: { results: SortResults, user: CliUser, setUser: Function }) {
  useEffect(() => {
    document.getElementById("results")?.scrollIntoView({ behavior: "smooth" });
  }, [])

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center"}} id="results">
      <Typography level="h1" sx={{ marginTop: "25px", textAlign: "center"}}>You were sorted into <Typography variant="solid" sx={{ bgcolor: results.color, color: results.house === "Freshbeast" ? "black" : undefined }}>House {results.house}!</Typography></Typography>
      <img src={imgSrc(`daedalus/crests/${results.image}`)} width="60%" style={{ aspectRatio: "1", objectFit: "contain", maxHeight: "500px", margin: "20px 0px" }} alt={`${results.house} crest`}/>
      <Card sx={{ width: "80%"}}>
        {results.blurb}
        {
          results.scores.sort((a, b) => b.score - a.score).map((house, i, arr) => {
            const highestScore = arr[0].score;
            const percent = (house.score / arr.reduce((p, c) => p + c.score, 0) * 100).toFixed(0)
            const visualPercent = (house.score / highestScore * 100).toFixed(0)
            return (
              <div style={{ width: `${visualPercent}%`, backgroundColor: house.color, padding: "10px"}} key={house.name}>
                <Typography sx={{color: "black", marginLeft: "10px" }}>{house.name} ({percent}%)</Typography>
              </div>
            )
          })
        }
      </Card>
      <br/>
      <div style={{ maxWidth: "95%", display: "flex", flexDirection: "column", alignItems: "center" }}>
        <Typography level="h1" margin={2}>{results.house} Lore</Typography>
        <YouTube
          videoId={results.lore}
          opts={{ allow, allowFullScreen: true, width: 560, height: 315, playerVars: { rel: 0 } }}
          iframeClassName="ytIframe"
          style={{ maxWidth: "100%" }}
        />
      </div>
      <br/>
      <Button onClick={() => {
        setUser(updateUser(user, results.newUser));
      }} size="lg">See what's next...</Button>
      <br/>
      <br/>
      <br/>
    </div>
  )
}

export default function Door() {
  const { setToast } = useContext(ToastContext)
  const { user, setUser } = useContext(UserContext)
  const [questions, setQuestions] = useState<SortQuestion[]>()
  const [reveal, setReveal] = useState(false)
  const [results, setResults] = useState<SortResults>();

  async function onEnd() {
    if (document.fullscreenElement) {
      await document.exitFullscreen().catch(() => {})
    }
    setTimeout(() => {
      setReveal(true)
      document.getElementById("survey")?.scrollIntoView({ behavior: "smooth" });
    }, 1000);
  }
  
  function sortFunction(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const answers = Array.from(form.values()).map(v => parseInt(v.toString()))
    daedalusAPI.sortMe(answers).then(info => {
      if (!setData(info, setToast, user, setUser)) return;      
      setResults(info);
    })
  }
  
  useEffect(() => {
    if (!user) return;
    daedalusAPI.getQuestions().then(info => {
      if (!setData(info, setToast, user, setUser)) return;      
      setQuestions(info);
    })
  }, [setToast, setUser, user])

  useEffect(() => {
    return () => {
      if (results && user) setUser(updateUser(user, results.newUser))
    }
  }, [setUser, results, user])

  if (!user) return <></>

  return (
    <div style={{ height: "100%" }}>
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
        <div style={{ maxWidth: "95%", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography level="h1" margin={2}>Before we begin...</Typography>
          <YouTube
            videoId="cTWoZxNr9OE"
            onEnd={onEnd}
            onError={onEnd}
            opts={{ allow, allowFullScreen: true, width: 560, height: 315, playerVars: { rel: 0 } }}
            iframeClassName="ytIframe"
            style={{ maxWidth: "100%" }}
          />
          <Typography>Watch the entire video to continue</Typography>
        </div>
      </div>
      { reveal && <Questions disabled={Boolean(results)} questions={questions ?? []} sortFunction={sortFunction}/> }
      { results && <Results results={results} setUser={setUser} user={user}/> }
    </div>
  )
}